import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import { packAlphaFrame } from "./packAlphaFrame";
import { align16, pickAvcCodecForVap } from "./vapConfig";

function patchMdat64To32(buffer: ArrayBuffer): ArrayBuffer {
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);
  let pos = 0;
  const len = bytes.length;

  while (pos + 8 <= len) {
    const size = view.getUint32(pos);
    const type = String.fromCharCode(
      bytes[pos + 4],
      bytes[pos + 5],
      bytes[pos + 6],
      bytes[pos + 7],
    );

    let boxLen = size;
    if (size === 1) {
      boxLen = Number(view.getBigUint64(pos + 8));
    } else if (size === 0) {
      boxLen = len - pos;
    }

    if (size === 1 && type === "mdat" && boxLen <= 0xffffffff) {
      view.setUint32(pos, boxLen);
    }

    if (boxLen <= 0) break;
    pos += boxLen;
  }

  return buffer;
}

function appendVapcBox(
  buffer: ArrayBuffer,
  frames: number,
  fps: number,
  srcW: number,
  srcH: number,
  encW: number,
  encH: number,
): ArrayBuffer {
  const json =
    `{"info":{"v":2,"f":${frames},"w":${srcW},"h":${srcH},"fps":${fps},` +
    `"videoW":${encW},"videoH":${encH},"orien":0,"isVapx":0,` +
    `"aFrame":[${srcW},0,${srcW},${srcH}],"rgbFrame":[0,0,${srcW},${srcH}]}}`;
  const payload = new TextEncoder().encode(json);
  const boxLen = 8 + payload.length;
  const out = new Uint8Array(buffer.byteLength + boxLen);
  out.set(new Uint8Array(buffer), 0);
  const view = new DataView(out.buffer, buffer.byteLength);
  view.setUint32(0, boxLen);
  out[buffer.byteLength + 4] = "v".charCodeAt(0);
  out[buffer.byteLength + 5] = "a".charCodeAt(0);
  out[buffer.byteLength + 6] = "p".charCodeAt(0);
  out[buffer.byteLength + 7] = "c".charCodeAt(0);
  out.set(payload, buffer.byteLength + 8);
  return out.buffer;
}

export interface EncodeVapMp4Options {
  frames: HTMLCanvasElement[];
  srcW: number;
  srcH: number;
  fps: number;
  bitrateKbps: number;
}

export async function encodeVapMp4({
  frames,
  srcW,
  srcH,
  fps,
  bitrateKbps,
}: EncodeVapMp4Options): Promise<Blob> {
  if (frames.length === 0) {
    throw new Error("VAP 编码需要至少一帧");
  }
  if (typeof VideoEncoder === "undefined" || typeof VideoFrame === "undefined") {
    throw new Error("当前浏览器不支持 WebCodecs，无法生成 VAP 视频");
  }

  const encW = align16(srcW * 2);
  const encH = align16(srcH);
  const frameDurationUs = Math.round(1_000_000 / fps);
  const avcCodec = pickAvcCodecForVap(encW, encH);

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: {
      codec: "avc",
      width: encW,
      height: encH,
    },
    // moov 在前，便于 ffprobe / Android MediaCodec 读取 avcC
    fastStart: "in-memory",
  });

  let sawAvcDecoderConfig = false;

  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      if (meta?.decoderConfig?.description?.byteLength) {
        sawAvcDecoderConfig = true;
      } else if (chunk.type === "key") {
        throw new Error("VAP 编码关键帧缺少 AVCDecoderConfigurationRecord（avcC）");
      }
      muxer.addVideoChunk(chunk, meta);
    },
    error: (err) => {
      throw err;
    },
  });

  encoder.configure({
    codec: avcCodec,
    width: encW,
    height: encH,
    bitrate: bitrateKbps * 1000,
    framerate: fps,
    latencyMode: "quality",
    // 必须 AVCC（length-prefixed NAL + description→avcC）；annexb 会导致 Android AnimView 解码失败
    avc: { format: "avc" },
  });

  for (let i = 0; i < frames.length; i++) {
    const packed = packAlphaFrame(frames[i], srcW, srcH);
    const timestamp = i * frameDurationUs;
    const videoFrame = new VideoFrame(packed, {
      timestamp,
      duration: frameDurationUs,
    });
    const keyFrame = i === 0;
    encoder.encode(videoFrame, { keyFrame });
    videoFrame.close();
  }

  await encoder.flush();
  encoder.close();
  if (!sawAvcDecoderConfig) {
    throw new Error("VAP 编码未产出 avcC 配置，无法生成合法 MP4");
  }
  muxer.finalize();

  let buffer = target.buffer;
  buffer = patchMdat64To32(buffer);
  buffer = appendVapcBox(buffer, frames.length, fps, srcW, srcH, encW, encH);

  return new Blob([buffer], { type: "video/mp4" });
}
